import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Trophy, Target, Scale, Swords, MessageSquare, Gauge, Brain, TrendingUp, Users, HelpCircle, BarChart3 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default Rules content
const defaultRulesContent = {
  en: {
    title: "Ladder Rules",
    intro: "A monthly ladder designed to encourage regular play, fair competition, and new partnerships.",
    basicsTitle: "Basics",
    basics: [
      "Players are responsible for booking courts and finding partners.",
      "Only matches played with 4 players participating in the ladder are counted.",
      "Match duration is 90 minutes.",
      "The ladder runs monthly and resets at the beginning of each month.",
      "Play with different partners to earn bonus points.",
      "Report the match score to the admins after the match."
    ],
    matchFormatTitle: "Match Format",
    matchFormat: [
      "Matches are played as best of 3 sets.",
      "A team must win 2 sets to win the match.",
      "Golden Point is played at 40–40 (no advantage).",
      "If a set reaches 6–6, a Tie Break is played to 7 points (win by 2).",
      "If sets are tied 1–1, a Super Tie Break is played to 10 points (win by 2)."
    ],
    scoringTitle: "Scoring",
    scoring: [
      "Win = 2 points",
      "Loss = 1 point",
      "Unique Partner = +1 point (awarded once per partner per month)"
    ],
    tiebreakerTitle: "Tie-breaker (Ranking)",
    tiebreakerText: "If players are tied in points, ranking is determined by game difference (games won − games lost). The Super Tie Break counts as one game.",
    footer: "Compete, but don't take yourself so seriously."
  },
  el: {
    title: "Κανόνες Ladder",
    intro: "Ένα μηνιαίο ladder σχεδιασμένο για να ενθαρρύνει το συχνό παιχνίδι, το fair play και τους νέους συμπαίκτες.",
    basicsTitle: "Βασικά",
    basics: [
      "Οι παίκτες είναι υπεύθυνοι για το κλείσιμο γηπέδου και την εύρεση συμπαικτών.",
      "Μετράνε μόνο αγώνες με 4 παίκτες που συμμετέχουν στο ladder.",
      "Το ladder τρέχει σε μηνιαία βάση και μηδενίζεται στην αρχή κάθε μήνα.",
      "Παίξτε με διαφορετικούς συμπαίκτες για να κερδίσετε bonus πόντους.",
      "Δηλώστε το σκορ στους admins μετά το τέλος του αγώνα."
    ],
    matchFormatTitle: "Μορφή Αγώνα",
    matchFormat: [
      "Οι αγώνες παίζονται ως best of 3 με μέγιστη διάρκεια 90 λεπτών.",
      "Μια ομάδα πρέπει να έχει 2 νικηφόρα σετ για να κερδίσει τον αγώνα.",
      "Στο 40–40 παίζεται Golden Point (χωρίς advantage).",
      "Αν το σετ φτάσει 6–6, παίζεται Tie Break στους 7 πόντους (διαφορά 2).",
      "Αν τα σετ είναι 1–1, παίζεται Super Tie Break στους 10 πόντους (διαφορά 2)."
    ],
    scoringTitle: "Βαθμολογία",
    scoring: [
      "Νίκη = 2 πόντοι",
      "Ήττα = 1 πόντος",
      "Μοναδικός συμπαίκτης = +1 πόντος (μετράει μία φορά ανά συμπαίκτη κάθε μήνα)"
    ],
    tiebreakerTitle: "Ισοβαθμία (Κατάταξη)",
    tiebreakerText: "Σε περίπτωση ισοβαθμίας, η κατάταξη καθορίζεται από τη διαφορά games (games won − games lost). Το Super Tie Break μετράει ως ένα game.",
    footer: "Παίξε ανταγωνιστικά, αλλά μην παίρνεις τον εαυτό σου τόσο στα σοβαρά."
  }
};

// Default Phidél Level content
const defaultLevelContent = {
  en: {
    title: "Understanding Your Level",
    intro: "Your Level shows your current playing level based on your match results.",
    understandingTitle: "What is Level?",
    understanding: [
      "Level is calculated from your performance against other players.",
      "It updates after every match.",
      "It reflects how you actually play, not how you rate yourself."
    ],
    calculationTitle: "How is it calculated?",
    calculationIntro: "We use a dynamic rating system based on:",
    calculation: [
      "Who you play against",
      "Whether you win or lose",
      "How close or dominant the match was",
      "How certain we already are about your level"
    ],
    calculationNotes: [
      "Beating stronger players increases your level more.",
      "Losing to weaker players decreases it more.",
      "Expected results (strong vs weak) have smaller impact."
    ],
    certaintyTitle: "What is Certainty?",
    certaintyIntro: "Certainty shows how confident the system is about your level.",
    certainty: [
      "More matches = higher certainty",
      "Less matches = lower certainty",
      "Higher certainty = smaller level changes"
    ],
    certaintyLevelsTitle: "Certainty Levels",
    certaintyLevels: [
      { level: "Very Low", meaning: "Not enough data yet" },
      { level: "Low", meaning: "Early estimate" },
      { level: "Medium", meaning: "Becoming reliable" },
      { level: "High", meaning: "Strong estimate" },
      { level: "Very High", meaning: "Very stable level" }
    ],
    matchesTitle: "How many matches do I need?",
    matches: [
      "5-10 matches: first meaningful level",
      "10-20 matches: good accuracy",
      "25+ matches: stable level"
    ],
    levelMeaningTitle: "Level Meaning",
    levelMeaning: [
      { range: "< 3.5", category: "Beginner" },
      { range: "3.5 - 4.5", category: "Low Intermediate" },
      { range: "4.5 - 5.5", category: "Intermediate" },
      { range: "5.5 - 6.5", category: "Advanced" },
      { range: "6.5+", category: "Competitive" }
    ],
    faqTitle: "Frequently Asked Questions",
    faq: [
      { question: "Why doesn't my level change much?", answer: "This happens when the result was expected, your certainty is already high, or you play similar-level opponents. This is normal - it means your level is stabilizing." },
      { question: "Why did my level change a lot?", answer: "This happens when you beat stronger players, you lose to weaker players, or you have low certainty (few matches)." },
      { question: "Can my level improve?", answer: "Yes. If you consistently perform better than your current level, your level will gradually increase. The system rewards consistent performance over time, not one-off results." }
    ],
    whyTitle: "Why this system?",
    why: ["Reflect real performance", "Encourage fair matches", "Avoid inflated self-ratings", "Create a balanced competitive environment"],
    footer: "Your level is not a label. It's a snapshot of your current performance. Play, improve, and let the system adjust."
  },
  el: {
    title: "Τι σημαίνει το Level σου;",
    intro: "Το Level δείχνει πώς παίζεις αυτή τη στιγμή, με βάση τα αποτελέσματα των αγώνων σου.",
    understandingTitle: "Τι είναι το Level;",
    understanding: [
      "Υπολογίζεται από την απόδοσή σου απέναντι σε άλλους παίκτες",
      "Ενημερώνεται μετά από κάθε αγώνα",
      "Δείχνει το πώς πραγματικά παίζεις, όχι το πώς νομίζεις ότι παίζεις"
    ],
    calculationTitle: "Πώς λειτουργεί;",
    calculationIntro: "Το σύστημα βασίζεται:",
    calculation: [
      "Με ποιους παίζεις",
      "Αν κερδίζεις ή χάνεις",
      "Πόσο κοντινό ή καθαρό ήταν το αποτέλεσμα",
      "Πόσο σίγουρο είναι ήδη το σύστημα για το Level σου"
    ],
    calculationNotes: [
      "Αν κερδίσεις πιο δυνατούς παίκτες, το Level σου ανεβαίνει περισσότερο",
      "Αν χάσεις από πιο αδύναμους, πέφτει περισσότερο",
      "Τα αναμενόμενα αποτελέσματα επηρεάζουν λιγότερο"
    ],
    certaintyTitle: "Τι είναι το Certainty;",
    certaintyIntro: "Certainty = Βεβαιότητα. Το Certainty δείχνει πόσο σίγουρο είναι το σύστημα για το Level σου.",
    certainty: [
      "Περισσότεροι αγώνες = μεγαλύτερο Certainty",
      "Λιγότεροι αγώνες = μικρότερο Certainty",
      "Μεγαλύτερο Certainty = μικρότερες αλλαγές στο Level"
    ],
    certaintyLevelsTitle: "Certainty Levels",
    certaintyLevels: [
      { level: "Very Low", meaning: "Δεν υπάρχουν αρκετά δεδομένα" },
      { level: "Low", meaning: "Αρχική εκτίμηση" },
      { level: "Medium", meaning: "Αρχίζει να γίνεται αξιόπιστο" },
      { level: "High", meaning: "Αρκετά σίγουρη εικόνα" },
      { level: "Very High", meaning: "Πολύ σταθερό Level" }
    ],
    matchesTitle: "Πόσους αγώνες χρειάζεσαι;",
    matches: [
      "5-10: παίρνεις μια πρώτη εικόνα",
      "10-20: το Level γίνεται αξιόπιστο",
      "25+: το Level σταθεροποιείται"
    ],
    levelMeaningTitle: "Τι σημαίνουν τα Levels;",
    levelMeaning: [
      { range: "< 3.5", category: "Beginner" },
      { range: "3.5 - 4.5", category: "Low Intermediate" },
      { range: "4.5 - 5.5", category: "Intermediate" },
      { range: "5.5 - 6.5", category: "Advanced" },
      { range: "6.5+", category: "Competitive" }
    ],
    faqTitle: "Συχνές Ερωτήσεις",
    faq: [
      { question: "Γιατί δεν αλλάζει πολύ το Level;", answer: "Αυτό συμβαίνει όταν το αποτέλεσμα ήταν αναμενόμενο, το Certainty σου είναι ήδη υψηλό, ή παίζεις με παίκτες κοντά στο Level σου. Είναι φυσιολογικό - σημαίνει ότι το Level σου σταθεροποιείται." },
      { question: "Γιατί άλλαξε πολύ το Level;", answer: "Αυτό συμβαίνει όταν κερδίζεις πιο δυνατούς παίκτες, χάνεις από πιο αδύναμους, ή έχεις χαμηλό Certainty (λίγους αγώνες)." },
      { question: "Μπορείς να βελτιωθείς;", answer: "Ναι. Αν παίζεις σταθερά καλύτερα από το τωρινό σου Level, το Level σου θα ανεβαίνει σταδιακά. Το σύστημα ανταμείβει τη συνέπεια στον χρόνο, όχι ένα μεμονωμένο αποτέλεσμα." }
    ],
    whyTitle: "Γιατί υπάρχει αυτό το σύστημα;",
    why: [
      "Δείχνει την πραγματική απόδοση",
      "Βοηθά να γίνονται πιο ισορροπημένα παιχνίδια",
      "Αποφεύγονται φουσκωμένα Levels",
      "Δημιουργείται πιο δίκαιο ανταγωνιστικό περιβάλλον"
    ],
    footer: "Το Level σου δεν είναι ταμπέλα. Είναι μια εικόνα του πώς παίζεις τώρα. Παίξε, βελτιώσου και άσε το σύστημα να σε ακολουθήσει."
  }
};

const Section = ({ icon: Icon, title, children }) => (
  <div className="bg-card rounded-xl border border-border p-6">
    <div className="flex items-center gap-3 mb-4">
      <div className="p-2 rounded-lg bg-muted">
        <Icon className="h-5 w-5 text-foreground" />
      </div>
      <h2 className="font-heading text-xl font-semibold text-foreground">{title}</h2>
    </div>
    <div className="text-muted-foreground leading-relaxed">{children}</div>
  </div>
);

// Rules Tab Content
const RulesContent = ({ content, loading }) => {
  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={`rules-skeleton-${i}`} className="bg-card rounded-xl border border-border p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-9 w-9 rounded-lg bg-muted" />
              <div className="h-6 w-32 bg-muted rounded" />
            </div>
            <div className="space-y-2">
              <div className="h-4 bg-muted rounded w-full" />
              <div className="h-4 bg-muted rounded w-3/4" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="rules-content">
      {/* Basics Section */}
      <Section icon={Trophy} title={content.basicsTitle}>
        <ul className="space-y-3">
          {content.basics.map((item, idx) => (
            <li key={`basics-${idx}`} className="flex items-start gap-3">
              <span className="h-1.5 w-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* Match Format Section */}
      <Section icon={Swords} title={content.matchFormatTitle}>
        <ul className="space-y-3">
          {content.matchFormat.map((item, idx) => (
            <li key={`format-${idx}`} className="flex items-start gap-3">
              <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 text-sm font-bold flex-shrink-0">
                {idx + 1}
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* Scoring Section */}
      <Section icon={Target} title={content.scoringTitle}>
        <ul className="space-y-3">
          {content.scoring.map((item, idx) => (
            <li key={`scoring-${idx}`} className="flex items-start gap-3">
              <span className="h-1.5 w-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* Tiebreaker Section */}
      <Section icon={Scale} title={content.tiebreakerTitle}>
        <p>{content.tiebreakerText}</p>
      </Section>

      {/* Footer Message */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-6 text-center">
        <MessageSquare className="h-8 w-8 text-slate-400 mx-auto mb-3" />
        <p className="text-slate-300 italic text-lg">
          "{content.footer}"
        </p>
      </div>
    </div>
  );
};

// Phidél Level Tab Content
const LevelContent = ({ content, language, loading }) => {
  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={`level-skeleton-${i}`} className="bg-card rounded-xl border border-border p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-9 w-9 rounded-lg bg-muted" />
              <div className="h-6 w-32 bg-muted rounded" />
            </div>
            <div className="space-y-2">
              <div className="h-4 bg-muted rounded w-full" />
              <div className="h-4 bg-muted rounded w-3/4" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="phidel-level-content">
      {/* Understanding Level */}
      <Section icon={Gauge} title={content.understandingTitle}>
        <ul className="space-y-3">
          {content.understanding.map((item, idx) => (
            <li key={`understanding-${idx}`} className="flex items-start gap-3">
              <span className="h-1.5 w-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* Calculation */}
      <Section icon={Brain} title={content.calculationTitle}>
        <p className="mb-3">{content.calculationIntro}</p>
        <ul className="space-y-2 mb-4">
          {content.calculation.map((item, idx) => (
            <li key={`calc-${idx}`} className="flex items-start gap-3">
              <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 text-xs font-bold flex-shrink-0">
                {idx + 1}
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mt-4">
          <p className="text-amber-800 dark:text-amber-200 font-medium mb-2">
            {language === 'en' ? 'Important:' : 'Σημαντικό:'}
          </p>
          <ul className="space-y-2 text-amber-700 dark:text-amber-300 text-sm">
            {content.calculationNotes.map((note, idx) => (
              <li key={`note-${idx}`} className="flex items-start gap-2">
                <span className="flex-shrink-0">•</span>
                <span>{note}</span>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* Certainty */}
      <Section icon={Target} title={content.certaintyTitle}>
        <p className="mb-3">{content.certaintyIntro}</p>
        <ul className="space-y-2 mb-4">
          {content.certainty.map((item, idx) => (
            <li key={`certainty-${idx}`} className="flex items-start gap-3">
              <span className="h-1.5 w-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* Certainty Levels Table */}
      <Section icon={BarChart3} title={content.certaintyLevelsTitle}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-3 font-semibold">Certainty</th>
                <th className="text-left py-2 px-3 font-semibold">
                  {language === 'en' ? 'Meaning' : 'Σημασία'}
                </th>
              </tr>
            </thead>
            <tbody>
              {content.certaintyLevels.map((item, idx) => (
                <tr key={`cert-level-${idx}`} className="border-b border-border/50">
                  <td className="py-2 px-3 font-medium">{item.level}</td>
                  <td className="py-2 px-3">{item.meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Matches Needed */}
      <Section icon={TrendingUp} title={content.matchesTitle}>
        <ul className="space-y-3">
          {content.matches.map((item, idx) => (
            <li key={`matches-${idx}`} className="flex items-start gap-3">
              <span className="h-1.5 w-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* Level Meaning Table */}
      <Section icon={Users} title={content.levelMeaningTitle}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-3 font-semibold">Level</th>
                <th className="text-left py-2 px-3 font-semibold">
                  {language === 'en' ? 'Category' : 'Κατηγορία'}
                </th>
              </tr>
            </thead>
            <tbody>
              {content.levelMeaning.map((item, idx) => (
                <tr key={`level-meaning-${idx}`} className="border-b border-border/50">
                  <td className="py-2 px-3 font-mono font-medium">{item.range}</td>
                  <td className="py-2 px-3">{item.category}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* FAQ */}
      <Section icon={HelpCircle} title={content.faqTitle}>
        <div className="space-y-4">
          {content.faq.map((item, idx) => (
            <div key={`faq-${idx}`} className="border-b border-border/50 pb-4 last:border-0 last:pb-0">
              <h3 className="font-medium text-foreground mb-2">{item.question}</h3>
              <p className="text-sm">{item.answer}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Why this system */}
      <Section icon={Target} title={content.whyTitle}>
        <ul className="space-y-3">
          {content.why.map((item, idx) => (
            <li key={`why-${idx}`} className="flex items-start gap-3">
              <span className="h-1.5 w-1.5 rounded-full bg-green-500 mt-2 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* Footer Message */}
      <div className="bg-gradient-to-br from-blue-900 to-slate-800 rounded-xl p-6 text-center">
        <MessageSquare className="h-8 w-8 text-blue-400 mx-auto mb-3" />
        <p className="text-slate-300 italic text-lg">
          "{content.footer}"
        </p>
      </div>
    </div>
  );
};

export default function Rules() {
  const { language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rulesContent, setRulesContent] = useState(defaultRulesContent);
  const [levelContent, setLevelContent] = useState(defaultLevelContent);
  const [loadingRules, setLoadingRules] = useState(true);
  const [loadingLevel, setLoadingLevel] = useState(true);

  // Get active tab from URL params, default to 'rules'
  const activeTab = searchParams.get('tab') === 'level' ? 'level' : 'rules';

  const setActiveTab = (tab) => {
    if (tab === 'rules') {
      searchParams.delete('tab');
    } else {
      searchParams.set('tab', tab);
    }
    setSearchParams(searchParams);
  };

  // Fetch rules content
  useEffect(() => {
    const fetchRulesContent = async () => {
      try {
        const res = await fetch(`${API_URL}/api/rules-content`);
        if (res.ok) {
          const data = await res.json();
          setRulesContent({
            en: { ...defaultRulesContent.en, ...(data.en || {}) },
            el: { ...defaultRulesContent.el, ...(data.el || {}) }
          });
        }
      } catch (error) {
        console.error('Failed to fetch rules content:', error);
        // Keep defaults on error
      } finally {
        setLoadingRules(false);
      }
    };
    fetchRulesContent();
  }, []);

  // Fetch level content
  useEffect(() => {
    const fetchLevelContent = async () => {
      try {
        const res = await fetch(`${API_URL}/api/phidel-level-content`);
        if (res.ok) {
          const data = await res.json();
          setLevelContent({
            en: { ...defaultLevelContent.en, ...(data.en || {}) },
            el: { ...defaultLevelContent.el, ...(data.el || {}) }
          });
        }
      } catch (error) {
        console.error('Failed to fetch level content:', error);
        // Keep defaults on error
      } finally {
        setLoadingLevel(false);
      }
    };
    fetchLevelContent();
  }, []);

  const rules = rulesContent[language];
  const level = levelContent[language];

  // Tab labels
  const tabLabels = {
    rules: language === 'el' ? 'Κανόνες' : 'Rules',
    level: 'Phidél Level'
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="rules-page">
      {/* Hero */}
      <div 
        className="relative rounded-2xl overflow-hidden mb-6 h-48"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1693614055184-97cdbe4ebd7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxwYWRlbCUyMHJhY2tldCUyMGJhbGwlMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc3MjEyMTkwNnww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className={`absolute inset-0 ${activeTab === 'level' ? 'bg-gradient-to-r from-blue-900/90 to-slate-900/80' : 'bg-slate-900/80'}`} />
        <div className="relative h-full flex items-center justify-center text-center px-6">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
              {activeTab === 'rules' ? rules.title : level.title}
            </h1>
            <p className="text-slate-300 max-w-xl">
              {activeTab === 'rules' ? rules.intro : level.intro}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-border">
        <button
          onClick={() => setActiveTab('rules')}
          className={`px-4 py-2 text-sm font-medium transition-colors relative ${
            activeTab === 'rules'
              ? 'text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="tab-rules"
        >
          {tabLabels.rules}
          {activeTab === 'rules' && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('level')}
          className={`px-4 py-2 text-sm font-medium transition-colors relative ${
            activeTab === 'level'
              ? 'text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="tab-level"
        >
          {tabLabels.level}
          {activeTab === 'level' && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
          )}
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'rules' ? (
        <RulesContent content={rules} loading={loadingRules} />
      ) : (
        <LevelContent content={level} language={language} loading={loadingLevel} />
      )}
    </div>
  );
}
